import { Globe, InstagramLogo, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader } from "@/components/common";
import { SUPPLIER_CATEGORIES, categoryIcon, categorySlug } from "@/lib/supplierCategories";

export default function PreferredSuppliers() {
  const { data, loading } = useGet("/preferred-suppliers");
  if (loading) {
    return <Loading label="Loading preferred suppliers" />;
  }
  const items = data || [];

  return (
    <div>
      <PageHeader
        eyebrow="Recommended"
        title="Preferred Suppliers"
        description="A source-backed Gledswood directory. Browsing never adds a supplier to My Suppliers."
      />
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
        data-testid="supplier-category-landing"
      >
        {SUPPLIER_CATEGORIES.map(({ name, icon: Icon }) => {
          const count = items.filter((item) => item.category === name).length;
          return (
            <a
              key={name}
              href={`#${categorySlug(name)}`}
              className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 transition-colors duration-200 hover:border-secondary hover:bg-muted"
              data-testid={`supplier-category-${categorySlug(name)}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted/50">
                <Icon size={17} className="text-secondary" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-primary">{name}</span>
                <span className="block mt-0.5 text-xs text-muted-foreground">{count} listed</span>
              </span>
            </a>
          );
        })}
      </div>
      <div className="mt-10 space-y-10">
        {SUPPLIER_CATEGORIES.map(({ name }) => (
          <CategorySection
            key={name}
            category={name}
            entries={items.filter((item) => item.category === name)}
          />
        ))}
      </div>
    </div>
  );
}

function CategorySection({ category, entries }) {
  const Icon = categoryIcon(category);
  return (
    <section id={categorySlug(category)} data-testid={`supplier-section-${categorySlug(category)}`}>
      <div className="flex items-end justify-between gap-4 border-b border-border pb-3">
        <h2 className="flex items-center gap-3 font-heading text-3xl text-primary">
          <Icon size={26} className="text-secondary" />
          {category}
        </h2>
        <span className="text-sm text-muted-foreground">{entries.length} listed</span>
      </div>
      {entries.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
          No preferred suppliers are currently listed in this category.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {entries.map((supplier) => (
            <SupplierCard key={supplier.id} supplier={supplier} />
          ))}
        </div>
      )}
    </section>
  );
}

function SupplierCard({ supplier }) {
  const Icon = categoryIcon(supplier.category);
  return (
    <article className="rounded-lg border border-border bg-card p-5" data-testid={`preferred-${supplier.id}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-accent">
        <Icon size={15} />
        {supplier.category}
      </div>
      <h3 className="mt-3 font-heading text-2xl text-primary">{supplier.name}</h3>
      {supplier.business && <p className="mt-1 text-sm text-muted-foreground">{supplier.business}</p>}
      <div className="mt-5 flex flex-wrap gap-4">
        {supplier.phone && (
          <a href={`tel:${supplier.phone.replaceAll(" ", "")}`} className="text-sm text-secondary transition-colors duration-200 hover:text-primary" data-testid={`call-${supplier.id}`}>
            <Phone size={16} className="mr-1 inline" />Call
          </a>
        )}
        {supplier.email && (
          <a href={`mailto:${supplier.email}`} className="text-sm text-secondary transition-colors duration-200 hover:text-primary" data-testid={`email-${supplier.id}`}>
            <EnvelopeSimple size={16} className="mr-1 inline" />Email
          </a>
        )}
        {supplier.website && (
          <a href={supplier.website} target="_blank" rel="noreferrer" className="text-sm text-secondary transition-colors duration-200 hover:text-primary" data-testid={`website-${supplier.id}`}>
            <Globe size={16} className="mr-1 inline" />Website
          </a>
        )}
        {supplier.instagram && (
          <a href={`https://instagram.com/${supplier.instagram}`} target="_blank" rel="noreferrer" className="text-sm text-secondary transition-colors duration-200 hover:text-primary" data-testid={`instagram-${supplier.id}`}>
            <InstagramLogo size={16} className="mr-1 inline" />Instagram
          </a>
        )}
      </div>
    </article>
  );
}
